// The flight, step by step (F507, decision 113): what the pilot who has
// the aircraft does next, from the booking to the handover, each step
// ticked from what the platform and the companion know. On the airframe's
// Now card in full; on the fleet's Your aircraft now as the next step
// alone. Nothing for the others.

import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { queries, type TechLogEntry } from "@/api";
import { rowFor } from "@/components/OnThisPc";
import { useHostEvents } from "@/host/events";
import { transferLine } from "@/host/format";
import { useHost } from "@/host/host";
import { hostQueries } from "@/host/queries";
import { formatRelative } from "@/lib/format";

/** The booking as the page or the fleet's glance has it. */
export type Booking = { holder: { id: string }; acquired_at: string; flying?: boolean | null; signed: boolean; signed_at?: string | null };

/** The last return, for the pilot who made it: the handover is theirs to write until the next booking. */
export type LastReturn = { pilotId: string | null; at: string | null; seq: number | null };

/** What the companion knows on this PC, under it alone. */
export type OnPc = { installed: number | null; onMachine: boolean; transfer: string | null; watching: boolean };

export type StepState = "done" | "now" | "later";

export type Step = { key: "booked" | "installed" | "preflight" | "fly" | "return" | "handover"; title: string; text: string; state: StepState };

/**
 * The six steps with their states, or null when this pilot has nothing to
 * do with the aircraft now (not theirs, not returned by them last).
 * `numbersOnly` says the platform's newest entry carries numbers and no
 * files (the sharing itself, before the first upload, decision 130): there
 * is nothing to install, the aircraft flies with the files on this PC.
 */
export function flightSteps(input: { booking: Booking | null; meId: string; reg: string; last: LastReturn; pc: OnPc | null; handoverAt: string | null; numbersOnly?: boolean }): Step[] | null {
  const { booking, meId, reg, last, pc, handoverAt, numbersOnly = false } = input;
  if (booking && booking.holder.id === meId) {
    const installed: Step =
      pc === null
        ? { key: "installed", title: "On your PC", text: "The app installed the newest entry into X-Plane when you booked.", state: "done" }
        : pc.transfer
          ? { key: "installed", title: "On your PC", text: `${pc.transfer}; the files go into X-Plane once it is here.`, state: "now" }
          : pc.installed !== null && pc.onMachine
            ? { key: "installed", title: "On your PC", text: `Entry ${pc.installed} is installed in X-Plane.`, state: "done" }
            : numbersOnly && pc.onMachine
              ? { key: "installed", title: "On your PC", text: `The platform's entry carries numbers only, no files: ${reg} flies with the files already on this PC, and the first upload from here brings the files to the platform.`, state: "done" }
              : numbersOnly
                ? { key: "installed", title: "On your PC", text: `The platform's entry carries numbers only, no files, and ${reg} is not in your X-Plane: the files come to the platform with the owner's first upload.`, state: "now" }
                : { key: "installed", title: "On your PC", text: "Not in X-Plane yet: press Book the aircraft again on the card below, X-Plane closed, to install it.", state: "now" };
    const signed = booking.signed;
    const flying = !!booking.flying;
    const notWatching = pc !== null && pc.onMachine && !pc.watching && !flying;
    return [
      { key: "booked", title: "Booked", text: `Yours since ${formatRelative(booking.acquired_at)}. Nobody else can fly it until you are done.`, state: "done" },
      installed,
      {
        key: "preflight",
        title: "Sign the pre-flight",
        text: signed ? `Signed ${booking.signed_at ? formatRelative(booking.signed_at) : "by you"}.` : "Read what the record says of the aircraft and the note of the previous pilot, then sign for it.",
        state: signed ? "done" : "now",
      },
      {
        key: "fly",
        title: "Fly",
        text: flying
          ? "You are flying it. When you are parked and shut down, close X-Plane: the app uploads the aircraft by itself."
          : `Start X-Plane, load ${reg} and fly. The app watches X-Plane and uploads the aircraft when you close it parked.${notWatching ? " The app is not watching X-Plane now: press Start the watch again on the card below." : ""}`,
        state: flying || (signed && installed.state === "done") ? "now" : "later",
      },
      { key: "return", title: "Close X-Plane", text: "Parked and shut down, close X-Plane. The app uploads the files and the numbers; the entry and the flight are written for every pilot.", state: "later" },
      { key: "handover", title: "Handover", text: "After the upload, a few lines for the next pilot: how the aircraft behaved, what you noticed, where you left it.", state: "later" },
    ];
  }
  if (!booking && last.pilotId === meId && last.at) {
    const written = handoverAt !== null && new Date(handoverAt).getTime() >= new Date(last.at).getTime();
    // Everything done: the guide leaves the page (Gianluca, 2026-10-30: "I don't see to keep seeing this for ever").
    if (written) return null;
    return [
      { key: "booked", title: "Booked", text: "", state: "done" },
      { key: "installed", title: "On your PC", text: "", state: "done" },
      { key: "preflight", title: "Pre-flight", text: "", state: "done" },
      { key: "fly", title: "Flown", text: "", state: "done" },
      { key: "return", title: "Uploaded", text: `Uploaded ${formatRelative(last.at)}${last.seq ? `, entry ${last.seq}` : ""}. The aircraft is available to the others.`, state: "done" },
      {
        key: "handover",
        title: "Handover",
        text: "A few lines for the next pilot, below: how the aircraft behaved, what you noticed, where you left it. They read them on their pre-flight.",
        state: "now",
      },
    ];
  }
  return null;
}

/** The step to do now, if any. */
export function nextStep(steps: Step[] | null): Step | null {
  return steps?.find((s) => s.state === "now") ?? null;
}

/** The latest handover written by this pilot, from the sheet. */
function handoverBy(log: TechLogEntry[] | undefined, meId: string): string | null {
  const mine = (log ?? []).filter((e) => e.kind === "handover" && e.pilot?.id === meId).map((e) => e.at);
  return mine.length ? mine.sort().at(-1)! : null;
}

/** When this pilot last wrote the handover, from the sheet; asked only while it is theirs to write (decision 120: the page hides the form on it). */
export function useHandoverAt(airframeId: string, meId: string, enabled: boolean): string | null {
  const log = useQuery({ ...queries.techLog(airframeId), enabled });
  return handoverBy(log.data?.items, meId);
}

/** The companion's knowledge of this airframe on this PC: the entry installed, the transfer under way, the watch. */
function useOnPc(airframeId: string): OnPc | null {
  const host = useHost();
  const status = useQuery({ ...hostQueries.status(host!), enabled: !!host });
  const info = useQuery({ ...hostQueries.info(host!), enabled: !!host });
  const events = useHostEvents();
  if (!host) return null;
  const row = rowFor(status.data, airframeId);
  if (!row) return { installed: null, onMachine: false, transfer: null, watching: false };
  const t = events.transfers[row.uuid] ?? null;
  return {
    installed: row.local?.installed?.seq ?? null,
    onMachine: row.on_machine,
    transfer: t ? transferLine(t) : null,
    watching: info.data?.session?.hs_uuid === row.uuid,
  };
}

/** The steps in full, for the airframe's Now card. */
export function FlightSteps({ airframeId, reg, booking, meId, last }: { airframeId: string; reg: string; booking: Booking | null; meId: string; last: LastReturn }) {
  const pc = useOnPc(airframeId);
  const handoverAt = useHandoverAt(airframeId, meId, !booking && last.pilotId === meId);
  // The platform's newest entry without files: the sharing itself, before the first upload (decision 130).
  const detail = useQuery({ ...queries.airframe(airframeId), enabled: pc !== null });
  const numbersOnly = !!detail.data?.latest_version && !detail.data.latest_version.sha256;
  const steps = flightSteps({ booking, meId, reg, last, pc, handoverAt, numbersOnly });
  if (!steps) return null;
  const after = steps[0]!.text === "";
  return (
    <div className="space-y-2 border-t border-line pt-3" data-testid="flight-steps" data-phase={after ? "returned" : "booked"}>
      <div className="flex flex-wrap items-baseline gap-2">
        <h3 className="font-semibold">{after ? "Your flight, returned" : "Your flight, step by step"}</h3>
        <span className="muted text-xs">{after ? "one thing left for the next pilot" : "ticked as you go; the app and the platform do the rest"}</span>
      </div>
      <ol className={`steps steps-flight${after ? " steps-flight-after" : ""}`} aria-label={`Your flight with ${reg}, step by step`}>
        {steps.map((s, i) => (
          <li key={s.key} className={`step step-${s.state}`} data-testid={`flight-step-${s.key}`} data-state={s.state}>
            <span className="step-mark" aria-hidden="true">
              {s.state === "done" ? "✓" : i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{s.title}</span>
                {s.state === "now" ? <span className="pill pill-info">now</span> : null}
              </div>
              {s.text ? <p className={`text-sm ${s.state === "now" ? "" : "muted"}`}>{s.text}</p> : null}
              {s.state === "now" && s.key === "preflight" ? (
                <Link to="/airframes/$id/preflight" params={{ id: airframeId }} className="btn btn-sm btn-primary mt-2" data-testid="now-preflight">
                  Open the pre-flight
                </Link>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** The next step alone, for a row of the fleet's Your aircraft now: a link to the pre-flight when that is it. */
export function NextStep({ airframeId, reg, booking, meId }: { airframeId: string; reg: string; booking: Booking | null; meId: string }) {
  const steps = flightSteps({ booking, meId, reg, last: { pilotId: null, at: null, seq: null }, pc: null, handoverAt: null });
  const next = nextStep(steps);
  if (!next) return null;
  if (next.key === "preflight") {
    return (
      <Link to="/airframes/$id/preflight" params={{ id: airframeId }} className="text-warn text-sm underline" data-testid="your-aircraft-preflight">
        next: sign the pre-flight
      </Link>
    );
  }
  return (
    <span className="text-sm" data-testid="your-aircraft-next">
      {next.key === "fly" && booking?.flying ? "you are flying it: close X-Plane parked and shut down when you are done" : next.key === "fly" ? "next: start X-Plane and fly" : next.text}
    </span>
  );
}
